import { useState, useRef, useCallback, useEffect } from "react";
import { useInView, useReducedMotion } from "motion/react";

interface VimeoFacadeProps {
  videoId: string;
  /** Extra query params appended to the Vimeo embed URL (e.g. "badge=0&autopause=0") */
  params?: string;
  title?: string;
  className?: string;
  /** Called with the iframe element once it has been mounted (useful for Vimeo tracking) */
  onIframeReady?: (iframe: HTMLIFrameElement) => void;
  /** 画面に入ったら音を消して勝手に流す。押さないと中身が分からない映像向け */
  autoPlayInView?: boolean;
  /** 実際に動き出したときに親へ知らせる。auto は自動、click は押された */
  onActivate?: (mode: "auto" | "click") => void;
  /** YouTube の動画 id。入れると Vimeo ではなく YouTube を出す (videoId は使わない) */
  youtubeId?: string;
}

/**
 * 同時に動く Vimeo は1本だけ。iframe には postMessage で止める指示を出せる。
 * 自前の video (common/ScrollVideo) とは別に持っている。あちらは要素を
 * 直接触れるが、こちらは iframe 越しにしか触れないため。
 */
let activeVimeo: HTMLIFrameElement | null = null;

function post(el: HTMLIFrameElement, method: "play" | "pause") {
  el.contentWindow?.postMessage(JSON.stringify({ method }), "*");
}

function claimVimeo(el: HTMLIFrameElement) {
  if (activeVimeo && activeVimeo !== el) post(activeVimeo, "pause");
  activeVimeo = el;
}

/**
 * Facade for Vimeo embeds.
 *
 * Renders a lightweight thumbnail + play button instead of a heavy iframe.
 * The real Vimeo player is loaded only when the user clicks play,
 * saving ~500 KB per embed on initial page load.
 */
export function VimeoFacade({
  videoId,
  params,
  title = "Vimeo video",
  className = "",
  onIframeReady,
  autoPlayInView = false,
  onActivate,
  youtubeId,
}: VimeoFacadeProps) {
  const [mode, setMode] = useState<"idle" | "auto" | "click">("idle");
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const inView = useInView(boxRef, { amount: 0.6 });
  const reduce = useReducedMotion();
  const activated = mode !== "idle";

  const handleClick = useCallback(() => {
    setMode("click");
    onActivate?.("click");
  }, [onActivate]);

  /* 画面に入ったら音を消して流す。出たら止める。
     少し置いてから出すのは、スクロールの途中で通り過ぎるだけの人に
     500KB の player を読ませないため */
  useEffect(() => {
    if (!autoPlayInView || reduce || mode === "click") return;
    if (!inView) {
      if (iframeRef.current && activeVimeo === iframeRef.current) post(iframeRef.current, "pause");
      return;
    }
    if (mode === "auto") {
      if (iframeRef.current) {
        claimVimeo(iframeRef.current);
        post(iframeRef.current, "play");
      }
      return;
    }
    const id = setTimeout(() => {
      setMode("auto");
      onActivate?.("auto");
    }, 500);
    return () => clearTimeout(id);
  }, [inView, autoPlayInView, reduce, mode, onActivate]);

  // Notify parent when iframe is mounted
  useEffect(() => {
    if (!activated || !iframeRef.current) return;
    claimVimeo(iframeRef.current);
    onIframeReady?.(iframeRef.current);
  }, [activated, onIframeReady]);

  /* 自動のときだけ音を消す。押した人には音を出す */
  const base = params ? `${params}&autoplay=1` : "autoplay=1";
  const iframeSrc = youtubeId
    ? `https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&rel=0&playsinline=1`
    : `https://player.vimeo.com/video/${videoId}?${base}${mode === "auto" ? "&muted=1" : ""}`;

  // Vimeo thumbnail URL — vumbnail.com is Vimeo's official thumbnail service
  const thumbnailUrl = youtubeId
    ? `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`
    : `https://vumbnail.com/${videoId}.jpg`;

  if (activated) {
    return (
      <div ref={boxRef} className={`relative w-full pt-[56.25%] ${className}`}>
        <iframe
          ref={iframeRef}
          src={iframeSrc}
          className="absolute top-0 left-0 w-full h-full rounded-lg"
          allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
          allowFullScreen
          title={title}
        />
      </div>
    );
  }

  return (
    <div ref={boxRef} className={`relative w-full pt-[56.25%] ${className}`}>
      <button
        type="button"
        onClick={handleClick}
        className="absolute inset-0 w-full h-full cursor-pointer group bg-black rounded-lg overflow-hidden border-0 p-0"
        aria-label={`${title}を再生`}
      >
        {/* Thumbnail */}
        <img
          src={thumbnailUrl}
          alt=""
          width={640}
          height={360}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300 group-hover:opacity-80"
        />

        {/* Play button overlay */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-16 h-16 md:w-20 md:h-20 bg-black/60 rounded-full flex items-center justify-center transition-transform duration-200 group-hover:scale-110">
            <svg
              viewBox="0 0 24 24"
              fill="white"
              className="w-7 h-7 md:w-9 md:h-9 ml-1"
              aria-hidden="true"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      </button>
    </div>
  );
}
